import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"
import "chartjs-adapter-date-fns"
import { subscribeToMarketPrices } from "../channels/market_prices_channel"

Chart.register(...registerables)

const PERIOD_HOURS = { "6h": 6, "1d": 24, "1w": 24 * 7, "1m": 24 * 30, "all": null }
const PERIOD_UNIT  = { "6h": "minute", "1d": "minute", "1w": "hour", "1m": "hour", "all": "hour" }

export default class extends Controller {
  static values   = { url: String }
  static targets  = ["canvas", "periodBtn", "legend"]

  async connect() {
    this.subscriptions = []
    this._currentPeriod = "all"

    const response = await fetch(this.urlValue, { headers: { "Accept": "application/json" } })
    if (!response.ok) return

    const payload = await response.json()
    this.series = payload.markets || []

    this.renderLegend()
    this.renderChart()

    this.series.forEach(s => {
      const sub = subscribeToMarketPrices(s.id, {
        received: (data) => this._appendLivePoint(s.id, data)
      })
      this.subscriptions.push(sub)
    })
  }

  disconnect() {
    if (this.chart) this.chart.destroy()
    this.subscriptions?.forEach(sub => sub.unsubscribe())
    this.subscriptions = []
  }

  setPeriod(event) {
    this._currentPeriod = event.currentTarget.dataset.period
    this.highlightButton(this._currentPeriod)
    if (this.chart) {
      this.chart.data.datasets = this.buildDatasets()
      this.chart.options.scales.x.time.unit = PERIOD_UNIT[this._currentPeriod] || "hour"
      this.chart.update()
    }
  }

  highlightButton(period) {
    this.periodBtnTargets.forEach(btn => {
      const active = btn.dataset.period === period
      btn.classList.toggle("bg-slate-700", active)
      btn.classList.toggle("text-white", active)
      btn.classList.toggle("text-slate-400", !active)
    })
  }

  renderLegend() {
    if (!this.hasLegendTarget) return
    this.legendTarget.innerHTML = this.series.map(s => `
      <div class="flex items-center gap-2">
        <span class="inline-block w-2.5 h-2.5 rounded-full shrink-0" style="background-color: ${s.color}"></span>
        <span class="text-xs font-semibold text-white truncate">${this._escape(s.label)}</span>
        <span class="text-xs font-bold text-slate-300 ml-1">${this._pct(s.current)}</span>
      </div>
    `).join("")
  }

  updateLegendCurrent(marketId, prob) {
    const idx = this.series.findIndex(s => s.id === marketId)
    if (idx === -1) return
    this.series[idx].current = prob
    const row = this.legendTarget.children[idx]
    if (row) row.lastElementChild.textContent = this._pct(prob)
  }

  buildDatasets() {
    const hours = PERIOD_HOURS[this._currentPeriod]
    const cutoff = hours ? Date.now() - hours * 60 * 60 * 1000 : null

    return this.series.map(s => {
      const labels = s.labels || []
      const yes = s.yes_prices || []
      const points = labels.map((t, i) => ({ x: t, y: yes[i] }))
                           .filter(p => !cutoff || new Date(p.x).getTime() >= cutoff)
      return {
        label: s.label,
        data: points,
        borderColor: s.color,
        backgroundColor: s.color + "22",
        borderWidth: 2,
        fill: false,
        tension: 0.25,
        pointRadius: 0,
        pointHitRadius: 8
      }
    })
  }

  renderChart() {
    const ctx = this.canvasTarget.getContext("2d")
    this.chart = new Chart(ctx, {
      type: "line",
      data: { datasets: this.buildDatasets() },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: "index" },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#1e293b",
            borderColor: "#475569",
            borderWidth: 1,
            titleColor: "#f1f5f9",
            bodyColor: "#cbd5e1",
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${(ctx.parsed.y * 100).toFixed(1)}%`
            }
          }
        },
        scales: {
          x: {
            type: "time",
            time: { unit: PERIOD_UNIT[this._currentPeriod] || "hour",
                    displayFormats: { minute: "dd MMM HH:mm", hour: "dd MMM HH:mm" } },
            grid: { color: "rgba(51, 65, 85, 0.3)" },
            ticks: { color: "#94a3b8", maxTicksLimit: 8 }
          },
          y: {
            min: 0, max: 1,
            grid: { color: "rgba(51, 65, 85, 0.3)" },
            ticks: { color: "#94a3b8", callback: v => `${(v * 100).toFixed(0)}%` }
          }
        }
      }
    })
  }

  _appendLivePoint(marketId, data) {
    const series = this.series.find(s => s.id === marketId)
    if (!series) return
    const ts = data.timestamp || new Date().toISOString()
    series.labels = series.labels || []
    series.yes_prices = series.yes_prices || []
    series.labels.push(ts)
    series.yes_prices.push(data.probability_yes)
    this.updateLegendCurrent(marketId, data.probability_yes)

    if (this.chart) {
      this.chart.data.datasets = this.buildDatasets()
      this.chart.update("none")
    }
  }

  _pct(v) {
    if (v == null) return "—"
    return `${(v * 100).toFixed(0)}%`
  }

  _escape(s) {
    return String(s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }
}
